import React, { useState, useEffect } from 'react'
import {
  Box,
  IconButton,
  Chip,
  Typography,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import { Search as SearchIcon, Clear as ClearIcon } from '@material-ui/icons'
import { debounce } from 'lodash'
import { colors } from '../../../../Themes/Colors'
import {
  SearchContainer,
  SearchFieldsWrapper,
  SearchField,
  ActiveFiltersWrapper,
  FilterChip,
} from './AdminSearchBar.styled'

export interface SearchField {
  key: string
  label: string
  placeholder?: string
  type?: 'text' | 'number' | 'date' | 'checkbox'
}

export interface SearchFilters {
  [key: string]: string | boolean
}

interface AdminSearchBarProps {
  fields: SearchField[]
  onSearch: (filters: SearchFilters) => void
  debounceMs?: number
  initialFilters?: SearchFilters
}

export const AdminSearchBar: React.FC<AdminSearchBarProps> = ({
  fields,
  onSearch,
  debounceMs = 300,
  initialFilters,
}) => {
  const [filters, setFilters] = useState<SearchFilters>(initialFilters || {})
  const [hasInitialized, setHasInitialized] = useState(false)

  // Only apply initial filters once on mount
  useEffect(() => {
    if (initialFilters && !hasInitialized) {
      setFilters(initialFilters)
      setHasInitialized(true)
    }
  }, [initialFilters, hasInitialized])

  // Create debounced function outside of useCallback
  const debouncedOnSearch = React.useMemo(
    () => debounce(onSearch, debounceMs),
    [onSearch, debounceMs]
  )

  useEffect(() => {
    debouncedOnSearch(filters)
  }, [filters, debouncedOnSearch])

  const handleFieldChange = (field: string, value: string | boolean) => {
    setFilters((prev) => {
      if (typeof value === 'string' && value.trim() === '') {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [field]: removed, ...rest } = prev
        return rest
      }
      return { ...prev, [field]: value }
    })
  }

  const clearFilter = (field: string) => {
    const fieldConfig = fields.find((f) => f.key === field)
    if (fieldConfig?.type === 'checkbox') {
      handleFieldChange(field, false)
    } else {
      handleFieldChange(field, '')
    }
  }

  const clearAllFilters = () => {
    setFilters({})
  }

  const activeFilterCount = Object.keys(filters).length

  return (
    <SearchContainer>
      <SearchFieldsWrapper>
        {fields.map((field) => {
          if (field.type === 'checkbox') {
            return (
              <FormControlLabel
                key={field.key}
                control={
                  <Checkbox
                    checked={Boolean(filters[field.key])}
                    onChange={(e) =>
                      handleFieldChange(field.key, e.target.checked)
                    }
                    color='primary'
                  />
                }
                label={field.label}
                style={{ marginRight: 16 }}
              />
            )
          }

          return (
            <SearchField
              key={field.key}
              label={field.label}
              placeholder={
                field.placeholder || `Search by ${field.label.toLowerCase()}`
              }
              variant='outlined'
              size='small'
              type={field.type || 'text'}
              value={filters[field.key] || ''}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchIcon
                    style={{ marginRight: 8, color: colors.mediumGrey }}
                  />
                ),
                endAdornment: filters[field.key] && (
                  <IconButton
                    size='small'
                    onClick={() => clearFilter(field.key)}
                    style={{ padding: 4 }}
                  >
                    <ClearIcon fontSize='small' />
                  </IconButton>
                ),
              }}
            />
          )
        })}
      </SearchFieldsWrapper>

      {activeFilterCount > 0 && (
        <ActiveFiltersWrapper>
          <Box display='flex' alignItems='center' style={{ marginRight: 16 }}>
            <Typography variant='body2' style={{ color: colors.mediumGrey }}>
              Active filters ({activeFilterCount}):
            </Typography>
          </Box>
          {Object.entries(filters).map(([key, value]) => {
            const field = fields.find((f) => f.key === key)
            let displayValue = value
            if (typeof value === 'boolean') {
              displayValue = value ? 'Yes' : 'No'
            }
            return (
              <FilterChip
                key={key}
                label={`${field?.label}: ${displayValue}`}
                onDelete={() => clearFilter(key)}
                size='small'
              />
            )
          })}
          <Chip
            label='Clear all'
            onClick={clearAllFilters}
            size='small'
            variant='outlined'
            style={{ marginLeft: 8 }}
          />
        </ActiveFiltersWrapper>
      )}
    </SearchContainer>
  )
}
